import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { Edit } from "@material-ui/icons";
import { useHistory, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { GET_SERVICE_REQUEST } from "../../../../graphql/serviceRequests/getServiceRequestsQuery";
import { CONSUMER_REQUEST_SCHEDULED_SERVICE_CHANGES } from "../../../../graphql/serviceRequests/consumerScheduledServiceMutations";
import { IAppState } from "../../../../store";
import { useSnackBar } from "../../../SnackBarContext/SnackBarContext";
import { SnackBarVariant } from "../../../SnackbarWrapper/SnackbarWrapper";
import { SimpleDateTimePicker } from "../../../SimpleDateTimePicker/intex";
import { getAppointmentIso, getVehicleLabel } from "./scheduledServiceUtils";
import ScheduledServiceJobSheet from "./ScheduledServiceJobSheet";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    paddingBottom: theme.spacing(12),
    backgroundColor: "#f5f5f5",
    minHeight: "100%",
  },
  header: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(3),
  },
  backButton: {
    marginRight: theme.spacing(2),
    textTransform: "none",
  },
  card: {
    marginBottom: theme.spacing(3),
    borderRadius: 8,
  },
  lockedField: {
    padding: theme.spacing(2),
    backgroundColor: "#f9f9f9",
    borderRadius: 8,
    height: "100%",
  },
  sectionTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    fontWeight: 600,
    marginBottom: theme.spacing(2),
  },
  detailLabel: {
    fontWeight: 600,
    color: "#666",
    fontSize: 12,
    textTransform: "uppercase",
    marginBottom: 4,
  },
  actionsCard: {
    borderRadius: 8,
    boxShadow: "0 -2px 12px rgba(0,0,0,0.08)",
  },
  actionRow: {
    display: "flex",
    gap: theme.spacing(2),
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
    },
  },
  cancelBtn: {
    flex: 1,
    textTransform: "none",
    fontWeight: 600,
    padding: theme.spacing(1.5, 2),
  },
  submitBtn: {
    flex: 1,
    backgroundColor: "#1976d2",
    color: "#fff",
    textTransform: "none",
    fontWeight: 600,
    padding: theme.spacing(1.5, 2),
    "&:hover": {
      backgroundColor: "#1565c0",
    },
  },
}));

const ModifyScheduledService: React.FC = () => {
  const classes = useStyles();
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const { t } = useTranslation();
  const showSnackbar = useSnackBar();
  const website = useSelector((state: IAppState) => state.consumerWebsiteReducer.consumerWebsite);

  const [appointmentDateIso, setAppointmentDateIso] = useState("");
  const [note, setNote] = useState("");

  const { data, loading } = useQuery(GET_SERVICE_REQUEST, {
    variables: { serviceRequestId: id },
    fetchPolicy: "network-only",
  });

  const service = data?.serviceRequest;
  const branchTimeZone = website?.branches?.find((b: any) => b.id === service?.branchId)?.timeZone || "UTC";

  useEffect(() => {
    const iso = getAppointmentIso(service);
    if (iso) {
      setAppointmentDateIso(iso);
    }
  }, [service]);

  const [requestChanges, { loading: submitting }] = useMutation(
    CONSUMER_REQUEST_SCHEDULED_SERVICE_CHANGES,
    {
      onCompleted: () => {
        showSnackbar({
          message: t("root:requestChangesSuccess", { defaultValue: "Change request sent for approval" }),
          variant: SnackBarVariant.SUCCESS,
        });
        history.push("/account/service-requests");
      },
      onError: (error) => {
        showSnackbar({ message: error.message, variant: SnackBarVariant.ERROR });
      },
    }
  );

  const handleSubmit = () => {
    requestChanges({
      variables: {
        input: {
          serviceId: id,
          appointmentDate: appointmentDateIso,
          note: note.trim() || undefined,
        },
      },
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  if (!service) {
    return (
      <Box p={3}>
        <Typography color="error">{t("snackBar:errorLoadingServiceRequests")}</Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Button className={classes.backButton} onClick={() => history.push(`/account/scheduled-service/${id}`)}>
          ← {t("misc:back", { defaultValue: "Back" })}
        </Button>
        <Typography variant="h4" style={{ fontWeight: 600 }}>
          {t("root:modifyServiceRequest", { defaultValue: "Modify service request" })}
        </Typography>
      </Box>

      <Card className={classes.card}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Box className={classes.lockedField}>
                <Typography className={classes.detailLabel}>{t("root:vehicle", { defaultValue: "Vehicle" })}</Typography>
                <Typography variant="body1" style={{ fontWeight: 600 }}>
                  {getVehicleLabel(service.vehicle)}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box className={classes.lockedField}>
                <Typography className={classes.detailLabel}>
                  {t("root:serviceCategory", { defaultValue: "Service category" })}
                </Typography>
                <Typography variant="body1" style={{ fontWeight: 600 }}>
                  {service.serviceCategory || "—"}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card className={classes.card}>
        <CardContent>
          <Typography className={classes.sectionTitle}>
            <Edit fontSize="small" color="primary" />
            {t("root:modifyDetails", { defaultValue: "Modify details" })}
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <SimpleDateTimePicker
                classId="flatpickr-container"
                date={appointmentDateIso}
                handleChange={(value) => {
                  if (value?.date) {
                    setAppointmentDateIso(value.date);
                  }
                }}
                required
                name="scheduledServiceAppointment"
                dateTitle={t("root:appointmentDate", { defaultValue: "Appointment date" })}
                timeTitle={t("root:time", { defaultValue: "Time" })}
                timeZone={branchTimeZone}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                variant="outlined"
                multiline
                rows={4}
                label={t("root:additionalNotes", { defaultValue: "Additional notes" })}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={t("root:explainChangesPlaceholder", {
                  defaultValue: "Explain why you need these changes...",
                })}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <ScheduledServiceJobSheet service={service} />

      <Card className={classes.actionsCard}>
        <CardContent>
          <Box className={classes.actionRow}>
            <Button
              className={classes.cancelBtn}
              variant="outlined"
              color="primary"
              onClick={() => history.push(`/account/scheduled-service/${id}`)}
              disabled={submitting}
            >
              {t("misc:cancel", { defaultValue: "Cancel" })}
            </Button>
            <Button
              className={classes.submitBtn}
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting || !appointmentDateIso}
            >
              {t("root:submitChanges", { defaultValue: "Submit changes" })}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ModifyScheduledService;
